// useCommandPalette — Cmd+K 명령 팔레트 상태 관리 훅 (U48)
//
// 책임:
//   - open / close / toggle 상태
//   - 입력 쿼리 관리
//   - prefix 분석 → 모드 자동 전환 ('>' / '/' / '@')
//   - 키보드 포커스 인덱스 관리 (↑↓)
//   - 200ms debounce 검색 쿼리 노출
//   - Cmd+K (Mac) / Ctrl+K (Win/Linux) 글로벌 단축키 등록

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';

// ─── 타입 정의 ───────────────────────────────────────────────

/** 팔레트 모드 */
export type CommandPaletteMode = 'command' | 'search' | 'navigate' | 'mixed';

/** 모드 prefix 상수 */
export const MODE_PREFIX_COMMAND = '>';
export const MODE_PREFIX_SEARCH = '/';
export const MODE_PREFIX_NAVIGATE = '@';

/** 디바운스 지연 시간 (ms) */
const DEBOUNCE_MS = 200;

// ─── 모드 판별 유틸 ──────────────────────────────────────────

/**
 * 입력 쿼리의 첫 문자(prefix)를 분석해 팔레트 모드를 결정한다.
 *
 * '>' → command
 * '/' → search
 * '@' → navigate
 * 그 외 → mixed (모든 모드 혼합)
 */
export function detectMode(query: string): CommandPaletteMode {
  if (query.startsWith(MODE_PREFIX_COMMAND)) return 'command';
  if (query.startsWith(MODE_PREFIX_SEARCH)) return 'search';
  if (query.startsWith(MODE_PREFIX_NAVIGATE)) return 'navigate';
  return 'mixed';
}

/**
 * prefix를 제거한 실제 검색어를 반환한다.
 * 모드가 mixed이면 원본 query 그대로 반환.
 */
export function stripPrefix(query: string, mode: CommandPaletteMode): string {
  if (mode === 'mixed') return query;
  return query.slice(1);
}

// ─── 훅 반환 타입 ─────────────────────────────────────────────

export interface UseCommandPaletteReturn {
  /** 팔레트 열림 여부 */
  open: boolean;
  /** 팔레트 열기 */
  openPalette: () => void;
  /** 팔레트 닫기 (쿼리·포커스 초기화) */
  closePalette: () => void;
  /** 현재 입력 쿼리 (prefix 포함) */
  query: string;
  /** 쿼리 변경 핸들러 */
  setQuery: (q: string) => void;
  /** prefix 제거된 실제 검색어 (mode='search' 시 itemsApi에 전달) */
  searchTerm: string;
  /** 200ms debounce된 searchTerm */
  debouncedSearchTerm: string;
  /** 현재 모드 */
  mode: CommandPaletteMode;
  /** 키보드 포커스 인덱스 (-1: 없음) */
  focusIndex: number;
  /** 총 결과 수 (CommandList가 주입) */
  resultCount: number;
  /** 총 결과 수 설정 */
  setResultCount: (n: number) => void;
  /** 포커스 인덱스 직접 설정 */
  setFocusIndex: (i: number) => void;
  /** ↑ 이동 */
  movePrev: () => void;
  /** ↓ 이동 */
  moveNext: () => void;
  /** 포커스 초기화 */
  resetFocus: () => void;
}

// ─── 훅 구현 ─────────────────────────────────────────────────

/**
 * 명령 팔레트 상태 관리 훅.
 * RequireAuth 영역 안에서 마운트하거나, CommandPalette 컴포넌트 내부에서 사용.
 */
export function useCommandPalette(): UseCommandPaletteReturn {
  const [open, setOpen] = useState(false);
  const [query, setQueryState] = useState('');
  const [focusIndex, setFocusIndex] = useState(-1);
  const [resultCount, setResultCount] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // debounce 타이머 ref
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 모드 및 prefix-stripped searchTerm 계산
  const mode = useMemo(() => detectMode(query), [query]);
  const searchTerm = useMemo(() => stripPrefix(query, mode), [query, mode]);

  // searchTerm이 변경될 때마다 200ms debounce
  useEffect(() => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm]);

  const openPalette = useCallback(() => {
    setOpen(true);
    setQueryState('');
    setFocusIndex(-1);
    setResultCount(0);
    setDebouncedSearchTerm('');
  }, []);

  const closePalette = useCallback(() => {
    setOpen(false);
    setQueryState('');
    setFocusIndex(-1);
    setResultCount(0);
    setDebouncedSearchTerm('');
    // 진행 중인 debounce 타이머 취소
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const setQuery = useCallback((q: string) => {
    setQueryState(q);
    // 쿼리 변경 시 포커스 초기화
    setFocusIndex(-1);
  }, []);

  const movePrev = useCallback(() => {
    setFocusIndex((prev) => {
      if (resultCount === 0) return -1;
      if (prev <= 0) return resultCount - 1;
      return prev - 1;
    });
  }, [resultCount]);

  const moveNext = useCallback(() => {
    setFocusIndex((prev) => {
      if (resultCount === 0) return -1;
      if (prev >= resultCount - 1) return 0;
      return prev + 1;
    });
  }, [resultCount]);

  const resetFocus = useCallback(() => {
    setFocusIndex(-1);
  }, []);

  return {
    open,
    openPalette,
    closePalette,
    query,
    setQuery,
    searchTerm,
    debouncedSearchTerm,
    mode,
    focusIndex,
    resultCount,
    setResultCount,
    setFocusIndex,
    movePrev,
    moveNext,
    resetFocus,
  };
}
